import type { BulkAction, TableAction } from '@shared/types/data-table'
import { Download, MoreHorizontal } from 'lucide-react'

export interface BaseActionHandlers<T> {
  onEdit?: (item: T) => void
  onDelete?: (item: T) => void
  onFlag?: (item: T) => void
  onAddNote?: (item: T) => void
  onCopyId?: (item: T) => void
  onExport?: (ids: string[]) => void
  onMore?: (ids: string[]) => void
}

export interface ActionGroup<T> {
  bulkActions: BulkAction[]
  rowActions: TableAction<T>[]
}

/**
 * Creates base actions that are common across most entities
 */
export function createBaseActions<T extends { id: string | number; status?: string }>(
  entityName: string,
  handlers: BaseActionHandlers<T>,
  options: {
    showEdit?: boolean
    showDelete?: boolean
    showFlag?: boolean
    showAddNote?: boolean
    showCopyId?: boolean
    showExport?: boolean
    showMore?: boolean
  } = {}
): ActionGroup<T> {
  const {
    showEdit = true,
    showDelete = false,
    showFlag = false,
    showAddNote = false,
    showCopyId = true,
    showExport = true,
    showMore = true,
  } = options

  const bulkActions: BulkAction[] = []
  const rowActions: TableAction<T>[] = []

  // Bulk actions
  if (showExport && handlers.onExport) {
    bulkActions.push({
      key: 'export',
      label: 'Export Selected',
      icon: <Download className="h-4 w-4" />,
      onClick: handlers.onExport,
    })
  }

  // Row actions
  if (showEdit && handlers.onEdit) {
    rowActions.push({
      key: 'edit',
      label: `Edit ${entityName}`,
      onClick: handlers.onEdit,
    })
  }

  if (showCopyId && handlers.onCopyId) {
    rowActions.push({
      key: 'copy-id',
      label: `Copy ${entityName} ID`,
      onClick: handlers.onCopyId,
    })
  }

  if (showAddNote && handlers.onAddNote) {
    rowActions.push({
      key: 'add-note',
      label: 'Add Note',
      onClick: handlers.onAddNote,
    })
  }

  if (showFlag && handlers.onFlag) {
    rowActions.push({
      key: 'flag',
      label: 'Flag',
      onClick: handlers.onFlag,
    })
  }

  if (showDelete && handlers.onDelete) {
    rowActions.push({
      key: 'delete',
      label: `Delete ${entityName}`,
      onClick: handlers.onDelete,
      variant: 'destructive',
    })
  }

  // Always add "More" action as the last bulk action if showMore is true
  if (showMore) {
    bulkActions.push({
      key: 'more',
      label: 'More Actions',
      icon: <MoreHorizontal className="h-4 w-4" />,
      onClick: handlers.onMore || (() => {}),
    })
  }

  return { bulkActions, rowActions }
}

/**
 * Utility to merge base actions with entity-specific actions
 * Ensures "More" action always appears last
 */
export function mergeActions<T>(
  baseActions: ActionGroup<T>,
  specificActions: Partial<ActionGroup<T>>
): ActionGroup<T> {
  const baseBulkActions = baseActions.bulkActions
  const specificBulkActions = specificActions.bulkActions || []

  // Remove "More" action from base actions if it exists
  const moreAction = baseBulkActions.find(action => action.key === 'more')
  const baseActionsWithoutMore = baseBulkActions.filter(action => action.key !== 'more')

  // Merge actions and add "More" at the end if it existed
  const mergedBulkActions = [
    ...baseActionsWithoutMore,
    ...specificBulkActions,
    ...(moreAction ? [moreAction] : [])
  ]

  return {
    bulkActions: mergedBulkActions,
    rowActions: [...baseActions.rowActions, ...(specificActions.rowActions || [])],
  }
}

/**
 * Utility to filter actions based on conditions
 */
export function filterActions<T>(
  actions: ActionGroup<T>,
  item?: T,
  selectedIds?: string[]
): ActionGroup<T> {
  return {
    bulkActions: actions.bulkActions.filter(action => {
      if (typeof action.disabled === 'function') {
        return !action.disabled(selectedIds || [])
      }
      return !action.disabled
    }),
    rowActions: actions.rowActions.filter(action => {
      if (typeof action.disabled === 'function' && item) {
        return !action.disabled(item) && (!action.condition || action.condition(item))
      }
      return !action.disabled && (!action.condition || !item || action.condition(item))
    }),
  }
}